import React, { Component } from 'react';
import { Fuel, Droplets, Flame, TrendingUp, TrendingDown, Equal, ExternalLink } from 'lucide-react';
import { AnimateOnScroll } from './AnimateOnScroll';
// ============================================================
// PRECIOS DE COMBUSTIBLES — ACTUALIZAR CADA VIERNES
// Fuente: https://micm.gob.do/direcciones/combustibles/avisos-semanales-de-precios/avisos-semanales-de-precios-de-combustibles/
//
// Vigencia: 28 de marzo al 03 de abril de 2026
// Última actualización: 28/03/2026
//
// trend: 0 = sin cambio, 1 = subió, -1 = bajó (vs semana anterior)
// ============================================================
const VIGENCIA = '28 Mar - 03 Abr 2026';
const initialPrices = [{
  name: 'Gas Licuado (GLP)',
  price: 'RD $137.20',
  icon: Flame,
  color: '#FCEE02',
  trend: 0
}, {
  name: 'Gasolina Premium',
  price: 'RD $290.10',
  icon: Fuel,
  color: '#EC2424',
  trend: 0
}, {
  name: 'Gasolina Regular',
  price: 'RD $272.50',
  icon: Fuel,
  color: '#FB900D',
  trend: 0
}, {
  name: 'Gasoil Óptimo',
  price: 'RD $242.10',
  icon: Droplets,
  color: '#EC2424',
  trend: 0
}, {
  name: 'Gasoil Regular',
  price: 'RD $224.80',
  icon: Droplets,
  color: '#FB900D',
  trend: 0
}];
const MICM_URL = 'https://micm.gob.do/direcciones/combustibles/avisos-semanales-de-precios/avisos-semanales-de-precios-de-combustibles/';
export function PricesSection() {
  // Componente de Indicador Visual de Tendencia
  const PriceIndicator = ({
    trend
  }: {
    trend: number;
  }) => {
    if (trend === 1) {
      // SUBE (Mal/Rojo)
      return <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-325">
          <TrendingUp className="w-3.5 h-3.5" data-id="element-326" />
          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-327">
            Sube
          </span>
        </div>;
    }
    if (trend === -1) {
      // BAJA (Bueno/Verde)
      return <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 border border-green-500/20" data-id="element-328">
          <TrendingDown className="w-3.5 h-3.5" data-id="element-329" />
          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-330">
            Baja
          </span>
        </div>;
    }
    // IGUAL (Estable/Verde)
    return <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 border border-green-500/20" data-id="element-331">
        <Equal className="w-3.5 h-3.5" data-id="element-332" />
        <span className="text-xs font-bold uppercase tracking-wider" data-id="element-333">
          Estable
        </span>
      </div>;
  };
  return <section className="py-16 sm:py-24 relative overflow-hidden transition-colors duration-300" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-334">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, var(--text-primary) 0, var(--text-primary) 1px, transparent 0, transparent 50%)',
      backgroundSize: '20px 20px'
    }} data-id="element-335" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-336">
        <AnimateOnScroll data-id="element-337">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-338">
            <div className="max-w-2xl" data-id="element-339">
              <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{
              color: 'var(--text-primary)'
            }} data-id="element-340">
                <span style={{
                color: 'var(--accent-yellow-text)'
              }} data-id="element-341">
                  Precios
                </span>{' '}
                Hoy
              </h2>
              <p className="text-xl border-l-4 border-[#EC2424] pl-6" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-342">
                Monitoreo oficial MICM para República Dominicana.
              </p>
            </div>
            <div className="flex flex-col items-end gap-3" data-id="element-343">
              <div className="backdrop-blur-sm border px-6 py-3 rounded-xl font-mono text-sm uppercase tracking-widest shadow-xl" style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-secondary)'
            }} data-id="element-344">
                <span className="text-[#FCEE02] mr-2" data-id="element-345">●</span> Vigencia:{' '}
                {VIGENCIA}
              </div>
              <a href={MICM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-[#FCEE02] transition-colors" style={{
              color: 'var(--text-muted)'
            }} data-id="element-346">
                Fuente: MICM
                <ExternalLink className="w-3 h-3" data-id="element-347" />
              </a>
            </div>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-id="element-348">
          {/* PRODUCTO PRINCIPAL: GLP */}
          <AnimateOnScroll className="lg:col-span-5 lg:row-span-2" direction="left" data-id="element-349">
            <div className="h-full relative group rounded-2xl p-1 bg-gradient-to-b from-[#FCEE02] to-[#FCEE02]/50 shadow-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-id="element-350">
              {/* Inner Card */}
              <div className="h-full w-full bg-[#111111] rounded-xl p-8 sm:p-10 flex flex-col justify-between relative overflow-hidden" data-id="element-351">
                {/* Diagonal Stripe Pattern inside GLP card */}
                <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, #ffffff 0, #ffffff 1px, transparent 0, transparent 10px)',
                backgroundSize: '14px 14px'
              }} data-id="element-352" />

                {/* Radial Glow behind icon */}
                <div className="absolute top-10 left-10 w-32 h-32 bg-[#FCEE02]/20 rounded-full blur-3xl pointer-events-none" data-id="element-353" />

                <div className="flex justify-between items-start relative z-10" data-id="element-354">
                  <div className="p-4 rounded-2xl border border-[#FCEE02]/30 bg-[#111111] shadow-[0_0_15px_rgba(252,238,2,0.15)]" data-id="element-355">
                    <Flame className="w-10 h-10 text-[#FCEE02]" strokeWidth={1.5} data-id="element-356" />
                  </div>
                  <PriceIndicator trend={initialPrices[0].trend} data-id="element-357" />
                </div>

                <div className="mt-24 relative z-10" data-id="element-358">
                  <h3 className="text-2xl sm:text-3xl font-black text-white uppercase mb-4 tracking-tight" data-id="element-359">
                    {initialPrices[0].name}
                  </h3>
                  <div className="flex items-baseline gap-3" data-id="element-360">
                    <span className="text-5xl sm:text-7xl font-black text-[#FCEE02] tracking-tighter drop-shadow-[0_0_10px_rgba(252,238,2,0.3)]" data-id="element-361">
                      {initialPrices[0].price}
                    </span>
                    <span className="text-gray-400 font-bold text-sm sm:text-base uppercase tracking-widest" data-id="element-362">
                      GALÓN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          {/* GRID COMBUSTIBLES LÍQUIDOS */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-363">
            {initialPrices.slice(1).map((fuel, index) => <AnimateOnScroll key={index} delay={index * 0.1} direction="right" data-id="element-364">
                <div className="group h-full p-6 sm:p-8 border transition-all duration-300 relative rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1" style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--border-color)'
            }} data-id="element-365">
                  {/* Top Colored Border */}
                  <div className="absolute top-0 left-0 w-full h-1.5 transition-transform duration-500 origin-left" style={{
                backgroundColor: fuel.color
              }} data-id="element-366" />

                  {/* Subtle hover gradient overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none" style={{
                background: `linear-gradient(135deg, ${fuel.color}, transparent)`
              }} data-id="element-367" />

                  <div className="flex justify-between items-start mb-12 relative z-10" data-id="element-368">
                    <div className="flex flex-col gap-2" data-id="element-369">
                      <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight max-w-[140px] leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-370">
                        {fuel.name}
                      </h3>
                    </div>
                    <PriceIndicator trend={fuel.trend} data-id="element-371" />
                  </div>

                  <div className="flex justify-between items-end relative z-10" data-id="element-372">
                    <div data-id="element-373">
                      <span className="block text-3xl sm:text-4xl font-black tracking-tighter mb-1" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-374">
                        {fuel.price}
                      </span>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-375">
                        por galón
                      </span>
                    </div>

                    {/* Prominent Icon with bg circle */}
                    <div className="p-3 rounded-full transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" style={{
                  backgroundColor: `${fuel.color}15`
                }} data-id="element-376">
                      <fuel.icon className="w-8 h-8" style={{
                    color: fuel.color
                  }} strokeWidth={1.5} data-id="element-377" />
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>)}
          </div>
        </div>
      </div>
    </section>;
}